/**
 * When to ask for feedback — and, far more often, when to shut up.
 *
 * The rule is "every 15 minutes", but the minutes that count are ACTIVE ones:
 * the tab visible, focused, and somebody actually doing something in it. A tab
 * left open overnight owes us nothing, and a prompt waiting behind it at
 * breakfast is the kind of thing people close accounts over.
 *
 * Everything here is about not being the thing that ruins a moment:
 *
 *   - never during a game, a puzzle, a lesson or a spectated board, because
 *     interrupting somebody mid-move is worse than never asking at all;
 *   - never while they are typing, in case the first keystroke of the answer
 *     lands in our dialog;
 *   - never in the first minutes of a first visit, when they have not seen
 *     enough to have an opinion;
 *   - and when it comes due at a bad moment it WAITS, and asks at the next
 *     natural pause, rather than cancelling and starting the clock again.
 *
 * "No, thanks" means never again — on this device and, once they sign in, on
 * every other one, which is why the answer is also written to the account.
 * "Not now" costs another fifteen active minutes. Three prompts in a day is
 * the ceiling regardless, and it is shared across tabs: the state lives in
 * localStorage, so four open tabs are one conversation rather than four.
 *
 * The UI is not here. This module decides WHEN and remembers WHAT WAS SAID;
 * app.ts owns the dialog, because that is where the modal helpers and the
 * translations already live.
 */

const KEY = "xq-fb-prompt";

/** 15 active minutes. Overridable for tests — see `thresholdMs`. */
const DEFAULT_THRESHOLD_MS = 15 * 60_000;

/** No input for this long and the person is not really here, tab focused or not. */
const IDLE_MS = 60_000;

/** A first visit gets this long before it is asked anything, whatever the clock says. */
const FIRST_VISIT_GRACE_MS = 5 * 60_000;

/** Three a day, and that is generous. */
const MAX_PER_DAY = 3;

/** How long one tab's claim to show the prompt lasts, so two cannot both show it. */
const CLAIM_MS = 30_000;

export type Choice = "never" | "sent";

interface State {
  /** active milliseconds accumulated, ever */
  activeMs: number;
  /** activeMs as it stood when we last prompted (or 0) */
  lastPromptAt: number;
  /** prompts shown today, and which day that is (local YYYY-MM-DD) */
  today: number;
  day: string;
  /** set once they have answered for good */
  choice: Choice | null;
  /** first time we ever saw this browser */
  firstSeen: number;
  /** a tab's claim to be the one showing the dialog */
  claimUntil: number;
}

const BLANK: State = {
  activeMs: 0, lastPromptAt: 0, today: 0, day: "", choice: null, firstSeen: 0, claimUntil: 0,
};

const dayKey = (t: number) => new Date(t).toISOString().slice(0, 10);

function read(): State {
  try {
    const raw = localStorage.getItem(KEY);
    const s = raw ? { ...BLANK, ...(JSON.parse(raw) as Partial<State>) } : { ...BLANK };
    // A day boundary resets the daily count wherever it is noticed, including
    // in a tab that was open across midnight.
    const today = dayKey(Date.now());
    if (s.day !== today) { s.day = today; s.today = 0; }
    if (!s.firstSeen) s.firstSeen = Date.now();
    return s;
  } catch {
    // Private mode, or storage disabled. The prompt then behaves as if this
    // were always a first visit, which errs towards asking less.
    return { ...BLANK, firstSeen: Date.now() };
  }
}

function write(s: State): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* nothing to do; the prompt simply will not persist */
  }
}

function update(fn: (s: State) => void): State {
  const s = read();
  fn(s);
  write(s);
  return s;
}

/**
 * The interval, in milliseconds, of active time between prompts.
 *
 * Read from localStorage so the end-to-end test can ask for a two-second
 * interval instead of waiting a quarter of an hour, which is the only
 * practical way to test a timer like this. Bounded, so a stray value cannot
 * turn it into a prompt every tick.
 */
function thresholdMs(): number {
  try {
    const raw = Number(localStorage.getItem("xq-fb-ms"));
    if (Number.isFinite(raw) && raw >= 1000 && raw <= 24 * 3600_000) return raw;
  } catch { /* fall through */ }
  return DEFAULT_THRESHOLD_MS;
}

/** True when this browser has already settled the question for good. */
export function answered(): boolean {
  return read().choice !== null;
}

/** Record what the person said, so we honour it next time. */
export function remember(choice: Choice): void {
  update((s) => { s.choice = choice; s.claimUntil = 0; });
}

/**
 * Adopt an answer the SERVER knows about, on sign-in.
 *
 * Someone who declined on their phone signs in on a laptop that has never
 * heard of them; without this it would start asking. The reverse — a local
 * "never" that the server has not heard — is pushed the other way by app.ts
 * when it saves the choice.
 */
export function adoptServerChoice(state: "declined" | "submitted" | null | undefined): void {
  if (state === "declined") remember("never");
  else if (state === "submitted") remember("sent");
}

/** "Not now": another fifteen active minutes, and one of today's three spent. */
export function snooze(): void {
  update((s) => { s.lastPromptAt = s.activeMs; s.today += 1; s.claimUntil = 0; });
}

/** Called when the dialog is actually shown, so the ceiling counts it once. */
function markShown(): void {
  update((s) => { s.lastPromptAt = s.activeMs; s.today += 1; s.claimUntil = 0; });
}

export interface PromptOptions {
  /** Is now a bad moment? A game, a puzzle, a lesson, a modal, a text field. */
  isBusy: () => boolean;
  /** Put the dialog on screen. Only ever called when it is a good moment. */
  show: () => void;
}

/** Everything the scheduler needs to stop, for tests and for teardown. */
export interface PromptHandle {
  stop: () => void;
  /** Exposed for the e2e: what the scheduler currently believes. */
  debug: () => State & { dueIn: number };
}

/**
 * Start counting active time and asking at the right moments.
 *
 * Returns a handle rather than nothing so a test can inspect the state and a
 * future screen can stop it.
 */
export function startFeedbackPrompt(opts: PromptOptions): PromptHandle {
  let lastInput = Date.now();
  const noteInput = () => { lastInput = Date.now(); };
  const events: (keyof WindowEventMap)[] = ["pointerdown", "keydown", "wheel", "touchstart"];
  for (const e of events) addEventListener(e, noteInput, { passive: true });

  // Seed firstSeen on the very first run, so the grace period starts now.
  update(() => { /* read() fills firstSeen */ });

  const tick = setInterval(() => {
    const now = Date.now();
    const active = document.visibilityState === "visible"
      && document.hasFocus()
      && now - lastInput < IDLE_MS;
    if (!active) return;

    const s = update((st) => { st.activeMs += 1000; });

    if (s.choice !== null) return;
    if (s.today >= MAX_PER_DAY) return;
    if (now - s.firstSeen < FIRST_VISIT_GRACE_MS) return;
    if (s.activeMs - s.lastPromptAt < thresholdMs()) return;
    // Due — but only at a moment that is not somebody's else's.
    if (opts.isBusy()) return;
    // One tab shows it. The claim is short so a tab that dies mid-prompt does
    // not silence the others for long.
    if (s.claimUntil > now) return;
    update((st) => { st.claimUntil = now + CLAIM_MS; });

    markShown();
    opts.show();
  }, 1000);

  return {
    stop: () => {
      clearInterval(tick);
      for (const e of events) removeEventListener(e, noteInput);
    },
    debug: () => {
      const s = read();
      return { ...s, dueIn: Math.max(0, thresholdMs() - (s.activeMs - s.lastPromptAt)) };
    },
  };
}
